<template>
  <div class="flex h-screen">
    <!-- Side Panel -->
    <div
      class="w-64 min-h-screen bg-gray-100 dark:bg-gray-900 border-r border-gray-300 dark:border-gray-700 transition-colors duration-200"
    >
      <div class="p-4 border-b border-gray-300 dark:border-gray-700 bg-transparent">
        <router-link
          to="/"
          class="flex items-center justify-center cursor-pointer p-1 logo-link"
        >
          <div class="flex items-center">
            <!-- Wrench icon -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-9 w-9 mr-1"
              viewBox="0 0 24 24"
            >
              <path
                d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"
                fill="#FFD700"
                stroke="#FFD700"
                stroke-width="0.5"
              />
            </svg>

            <!-- Text logo -->
            <div class="flex items-center">
              <span class="text-3xl font-bold text-blue-500">dev</span>
              <span class="text-3xl font-bold text-green-500">tools</span>
            </div>
          </div>
        </router-link>
      </div>

      <nav class="mt-4">
        <div class="flex flex-col">
          <template
            v-for="item in menuItems"
            :key="item.name"
          >
            <!-- Folder -->
            <template v-if="(item as FolderMenuItem).type === 'folder'">
              <div class="flex flex-col">
                <div
                  class="flex items-center px-4 py-3 text-gray-600 dark:text-gray-300 font-medium"
                >
                  <component
                    :is="item.icon"
                    class="w-5 h-5 mr-3"
                  />
                  <span>{{ item.name }}</span>
                </div>
                <!-- Nested Items -->
                <router-link
                  v-for="subItem in (item as FolderMenuItem).children"
                  :key="subItem.path"
                  :to="subItem.path"
                  class="flex items-center px-8 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800"
                  :class="{ 'bg-gray-200 dark:bg-gray-800 font-medium': isActive(subItem.path) }"
                >
                  <component
                    :is="subItem.icon"
                    class="w-5 h-5 mr-3"
                  />
                  <span>{{ subItem.name }}</span>
                </router-link>
              </div>
            </template>
            <!-- Regular Menu Item -->
            <router-link
              v-else
              :to="(item as RegularMenuItem).path"
              class="flex items-center px-4 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800"
              :class="{
                'bg-gray-200 dark:bg-gray-800 font-medium': isActive((item as RegularMenuItem).path)
              }"
            >
              <component
                :is="item.icon"
                class="w-5 h-5 mr-3"
              />
              <span>{{ item.name }}</span>
            </router-link>
          </template>
        </div>
      </nav>

      <!-- User Info and Logout -->
      <div class="absolute bottom-0 w-64 p-4 border-t border-gray-300 dark:border-gray-700">
        <div
          v-if="user"
          class="flex items-center mb-3"
        >
          <!-- Simple colored circle with initials -->
          <div
            class="w-8 h-8 rounded-full flex items-center justify-center mr-2"
            :style="{
              backgroundColor: getUserColor,
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.875rem'
            }"
          >
            {{ getUserInitials }}
          </div>
          <div class="flex flex-col">
            <span class="text-sm font-medium text-gray-800 dark:text-white">{{ user.name }}</span>
            <span class="text-xs text-gray-600 dark:text-gray-400 max-w-[180px] truncate">{{
              user.email
            }}</span>
          </div>
        </div>

        <!-- Theme toggle -->
        <div class="flex items-center justify-between mb-3 px-1">
          <span class="text-sm text-gray-600 dark:text-gray-400">Theme</span>
          <button
            class="flex items-center justify-center rounded-full w-10 h-5 bg-gray-300 dark:bg-gray-600 relative"
            @click="theme.toggleTheme"
          >
            <!-- Sun icon for dark mode (switch to light) -->
            <svg
              v-if="theme.isDark.value"
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 text-yellow-300 absolute right-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <!-- Moon icon for light mode (switch to dark) -->
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 text-blue-400 absolute left-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
            <div
              class="w-4 h-4 rounded-full absolute transition-all duration-300 transform bg-gray-200"
              :class="theme.isDark.value ? 'translate-x-2' : '-translate-x-2'"
            ></div>
          </button>
        </div>

        <button
          class="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-rose-500 dark:bg-red-600 hover:bg-rose-600 dark:hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 dark:focus:ring-red-500 transition-colors duration-200"
          @click="handleLogout"
        >
          <LogoutIcon class="w-4 h-4 mr-2" />
          Logout
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-auto bg-white dark:bg-gray-800">
      <router-view></router-view>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, inject } from 'vue'
import { useRoute } from 'vue-router'
import {
  LogOut01Icon,
  FilterLinesIcon,
  Settings01Icon,
  FolderIcon,
  PackageIcon,
  File01Icon,
  ActivityIcon,
  CheckCircleIcon
} from '@gohighlevel/ghl-icons/24/outline'
import auth from '../../services/auth/auth'
import { useTheme } from '../../utils/useTheme'

interface MenuItem {
  name: string
  icon: string
}

interface RegularMenuItem extends MenuItem {
  type?: 'item'
  path: string
}

interface FolderMenuItem extends MenuItem {
  type: 'folder'
  children: RegularMenuItem[]
}

type MenuItems = (RegularMenuItem | FolderMenuItem)[]

export default defineComponent({
  name: 'SidePanel',
  components: {
    LogoutIcon: LogOut01Icon,
    FilterIcon: FilterLinesIcon,
    SettingsIcon: Settings01Icon,
    FolderIcon,
    PackageIcon,
    FileIcon: File01Icon,
    ActivityIcon,
    CheckCircleIcon
  },
  setup() {
    const route = useRoute()
    const theme = inject('theme', useTheme())

    const menuItems: MenuItems = [
      {
        name: 'Package Versions',
        path: '/packages',
        icon: 'PackageIcon'
      },
      {
        name: 'Versioning',
        type: 'folder',
        icon: 'FolderIcon',
        children: [
          {
            name: 'API Calls',
            path: '/request-filter',
            icon: 'FilterIcon'
          },
          {
            name: 'PubSub Events',
            path: '/workers-filter',
            icon: 'SettingsIcon'
          }
        ]
      },
      {
        name: 'IAM Configuration',
        path: '/iam-config',
        icon: 'SettingsIcon'
      },
      {
        name: 'Audit Logs',
        path: '/audit-logs',
        icon: 'FileIcon'
      },
      {
        name: 'API Dependency Graph',
        path: '/dependency-graph',
        icon: 'ActivityIcon'
      },
      {
        name: 'Quality Gates',
        path: '/quality-gates',
        icon: 'CheckCircleIcon'
      }
    ]

    const isActive = (path: string) => route.path === path

    // Get the logged-in user
    const user = computed(() => auth.getUser())

    // Logout handler
    const handleLogout = () => {
      auth.logout()
    }

    const getUserInitials = computed(() => {
      if (user.value && user.value.name) {
        const initials = user.value.name
          .split(' ')
          .map((word) => word[0])
          .join('')
        return initials.toUpperCase()
      }
      return ''
    })

    const getUserColor = computed(() => {
      if (user.value && user.value.name) {
        const colors = ['#FF5757', '#FFB800', '#00D084', '#0083FF', '#7A5AF8']
        const index =
          user.value.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) %
          colors.length
        return colors[index]
      }
      return '#ccc'
    })

    return {
      menuItems,
      isActive,
      user,
      handleLogout,
      getUserInitials,
      getUserColor,
      theme
    }
  }
})
</script>

<style scoped>
.router-link-active:not(.logo-link) {
  @apply bg-gray-200 dark:bg-gray-800;
  font-size: 1rem;
}

.router-link {
  font-size: 1rem;
  @apply text-gray-700 dark:text-gray-300;
}

.flex.items-center {
  font-size: 1rem;
}

.logo-link.router-link-active {
  background-color: transparent !important;
}
</style>
